<app-pias-layout>
  <div pias-layout-content>
    @if (this.setSprinner()) {
    <div class="pias-spinner">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    }
    <div class="content">
      <div class="maintenance-container">

        <!-- ── Page header (design 1a: accent rail + title) ────── -->
        <div class="page-header-section">
          <span class="header-rail"></span>
          <div class="title-wrapper">
            <h4 class="page-title">Adviser Maintenance</h4>
            <p class="page-subtitle">
              @if (activeSection()) {
              @if(isReadOnly() === false){
              Editing <strong>{{ activeSection()?.title }}</strong>
              }
              }
            </p>
          </div>
        </div>

        <!-- ── Selected advisor summary ────────────────────────── -->
        <div class="advisor-summary">
          <!-- Badge with the rep status directly beneath it — same palette as the
             dashboard's status chips / stat tiles. -->
          <div class="advisor-identity">
            <span class="advisor-avatar">
              <mat-icon>account_box</mat-icon>
            </span>
            @if (statusLabel()) {
            <span class="advisor-status-chip" [ngClass]="statusClass()">
              <mat-icon>{{ statusIcon() }}</mat-icon>
              {{ statusLabel() }}
            </span>
            }
          </div>
          <!-- Short identifiers first, then the wide tiles (branch / FSD / FSM) whose
             values are long enough to need the extra room. -->
          <div class="advisor-fields">
            <div class="advisor-field">
              <span class="advisor-label">Representative Name</span>
              <span class="advisor-value" [title]="advisor().representativeName">{{ advisor().representativeName
                }}</span>
            </div>
            <div class="advisor-field">
              <span class="advisor-label">Preferred Name</span>
              <span class="advisor-value" [title]="advisor().preferredName">{{ advisor().preferredName }}</span>
            </div>
            <div class="advisor-field">
              <span class="advisor-label">Rep Code</span>
              <span class="advisor-value" [title]="advisor().representativeCode">{{ advisor().representativeCode
                }}</span>
            </div>
            <div class="advisor-field is-wide">
              <span class="advisor-label">Branch</span>
              <span class="advisor-value" [title]="advisor().branch">{{ advisor().branch }}</span>
            </div>
            <div class="advisor-field is-wide">
              <span class="advisor-label">Financial Services Director (FSD)</span>
              <span class="advisor-value" [title]="advisor().financialServicesDirector">{{
                advisor().financialServicesDirector }}</span>
            </div>
            <div class="advisor-field is-wide">
              <span class="advisor-label">Financial Services Manager (FSM)</span>
              <span class="advisor-value" [title]="advisor().financialServicesManager">{{
                advisor().financialServicesManager }}</span>
            </div>
          </div>
        </div>

        <!-- ── Section picker (design 1a: top-rail cards) ──────── -->
        @if (!activeSection()) {
        <button type="button" class="dashboard-back-btn" (click)="goToDashboard()">
          <mat-icon>arrow_back</mat-icon>
          Back to Dashboard
        </button>
        <div class="section-grid">
          @for (section of sections; track section.id; let i = $index) {
          <button type="button" class="section-card" (click)="openSection(section.id)" [style.--c1]="themeFor(i).c1"
            [style.--c2]="themeFor(i).c2" [style.--soft]="themeFor(i).soft" [style.--tint]="themeFor(i).tint"
            [style.--glow]="themeFor(i).glow">
            <span class="section-card-body">
              <span class="section-card-head">
                <span class="section-card-icon">
                  <mat-icon>{{ section.icon }}</mat-icon>
                </span>
                <span class="section-card-id">
                  <span class="section-card-title">{{ section.title }}</span>
                  <!-- <span class="section-card-tag">{{ section.tag }}</span> -->
                </span>
              </span>
              <span class="section-card-desc">{{ section.description }}</span>
            </span>
            <span class="section-card-arrow">
              <mat-icon>chevron_right</mat-icon>
            </span>
          </button>
          }
        </div>
        }

        <!-- ── Focused single-section editor ───────────────────── -->
        @if (activeSection(); as section) {
        <div class="section-editor" [formGroup]="maintenanceForm">

          <button type="button" class="back-link" (click)="backToSections()">
            <mat-icon>arrow_back</mat-icon>
            Back
          </button>

          <div class="editor-card">
            <div class="editor-header">
              <span class="editor-header-icon">
                <mat-icon>{{ section.icon }}</mat-icon>
              </span>
              <div class="editor-header-text">
                <h5 class="editor-title">{{ section.title }}</h5>
                <p class="editor-desc">{{ section.description }}</p>
              </div>
            </div>

            <div class="editor-body">
              @switch (section.id) {
              @case ('branchDetails') {
              <app-maintenance-branch [formGroup]="branchDetailsForm" [readonly]="isReadOnly"
                (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-maintenance-branch>
              }
              @case ('personalDetails') {
              <div class="editor-subheader" role="button" tabindex="0" (click)="toggleSub('personal')"
                (keydown.enter)="toggleSub('personal')" (keydown.space)="toggleSub('personal'); $event.preventDefault()"
                [attr.aria-expanded]="openSubs().personal">
                <span class="editor-header-icon">
                  <mat-icon>person</mat-icon>
                </span>
                <div class="editor-header-text">
                  <h5 class="editor-title">Personal Particulars</h5>
                  <p class="editor-desc">Identity, nationality and personal details.</p>
                </div>
                <mat-icon class="editor-subheader-chevron" [class.open]="openSubs().personal">expand_more</mat-icon>
              </div>
              <div class="sub-collapse" [class.open]="openSubs().personal">
                <app-personal-details [formGroup]="personalDetailsForm" [readonly]="isReadOnly"
                  (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-personal-details>
              </div>

              <div class="editor-subheader" role="button" tabindex="0" (click)="toggleSub('spouse')"
                (keydown.enter)="toggleSub('spouse')" (keydown.space)="toggleSub('spouse'); $event.preventDefault()"
                [attr.aria-expanded]="openSubs().spouse">
                <span class="editor-header-icon">
                  <mat-icon>wc</mat-icon>
                </span>
                <div class="editor-header-text">
                  <h5 class="editor-title">Spouse Details</h5>
                  <p class="editor-desc">Spouse identity, nationality and employment.</p>
                </div>
                <mat-icon class="editor-subheader-chevron" [class.open]="openSubs().spouse">expand_more</mat-icon>
              </div>
              <div class="sub-collapse" [class.open]="openSubs().spouse">
                <app-spouse-details [formGroup]="spouseDetailsForm" [readonly]="isReadOnly" (save)="saveSection($event)"
                  [maritalStatus]="personalDetailsForm.get('maritalStatus')?.value"></app-spouse-details>
              </div>

              <div class="editor-subheader" role="button" tabindex="0" (click)="toggleSub('contact')"
                (keydown.enter)="toggleSub('contact')" (keydown.space)="toggleSub('contact'); $event.preventDefault()"
                [attr.aria-expanded]="openSubs().contact">
                <span class="editor-header-icon">
                  <mat-icon>phone</mat-icon>
                </span>
                <div class="editor-header-text">
                  <h5 class="editor-title">Contact Details</h5>
                  <p class="editor-desc">Residential address, phone numbers and email.</p>
                </div>
                <mat-icon class="editor-subheader-chevron" [class.open]="openSubs().contact">expand_more</mat-icon>
              </div>
              <div class="sub-collapse" [class.open]="openSubs().contact">
                <app-contact-details [formGroup]="contactDetailsForm" [readonly]="isReadOnly"
                  (save)="saveSection($event)"></app-contact-details>
              </div>

              <div class="section-attachment multiple-file-attachment">
                <label class="field-label"> Personal Particulars Supporting Document </label>
                <!-- File input - only usable in Edit Mode -->
                <input #personalSupportFile type="file" hidden accept=".pdf,.jpg,.jpeg,.png" [readonly]="isReadOnly()"
                  (change)="onMultipleFileSelected( 
      $event, 
      personalDetailsForm, 
      'personalSupportingDocument')" />

                <!-- Existing / uploaded documents -->
                @if (personalSupportingDocuments().length > 0) {
                @for (document of personalSupportingDocuments(); track document.id) {
                <div class="file-row">
                  <div class="file-info">
                    <mat-icon>upload_file</mat-icon>
                    <span class="file-name"> {{ document.fileName }} </span>
                  </div>
                  @if (!isReadOnly()) {
                  <button type="button" class="file-clear" (click)="removeFile(document)">
                    <mat-icon>close</mat-icon>
                  </button>
                  }
                  <button type="button" class="file-clear" (click)="previewAttachment([
                  document.id.toString(),
                  document.fileName])">
                    <mat-icon>visibility</mat-icon>
                  </button>
                </div>
                }
                <!-- Add another file - ONLY in Edit Mode -->
                @if (!isReadOnly()) {
                <button type="button" class="file-add" (click)="personalSupportFile.click()">
                  <mat-icon>add</mat-icon>
                  <span>Add another file</span>
                </button>
                }
                } @else {
                <!-- Choose file - ONLY in Edit Mode -->
                @if (!isReadOnly()) {
                <div class="file-drop" (click)="personalSupportFile.click()">
                  <mat-icon>upload_file</mat-icon>
                  <span class="file-drop-text">Choose a file</span>
                </div>
                }
                }
                @if (fileError) {
                <mat-error>{{ fileError }}</mat-error>
                }

              </div>
              }
              @case ('regulatoryProfessionalQualifications') {
              <app-regulatory-professional-qualifications [formGroup]="regulatoryProfessionalQualifications"
                [readonly]="isReadOnly" (save)="saveSection($event)"
                (preview)="previewAttachment($event)"></app-regulatory-professional-qualifications>
              }
              @case ('insuranceProviders') {
              <app-maintenance-insurance-providers [formArray]="insuranceProvidersArray" [readonly]="isReadOnly"
                (save)="saveSection($event)"></app-maintenance-insurance-providers>
              }
              @case ('businessInterests') {
              <app-business-interests [formGroup]="businessInterests" [readonly]="isReadOnly"
                (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-business-interests>
              }
              @case ('generalInformation') {
              <app-general-information [formGroup]="generalInformation" [readonly]="isReadOnly"
                (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-general-information>
              }
              @case ('fitandProperCriteria') {
              <app-fit-propercriteria [formGroup]="fitandProperCriteria" [readonly]="isReadOnly"
                (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-fit-propercriteria>
              }
              @case ('additionalAttachments') {
              <app-maintenance-attachments [formGroup]="additionalAttachments" [readonly]="isReadOnly"
                (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-maintenance-attachments>
              }
              @case ('allAttachments') {
              <app-maintenance-all-attachments></app-maintenance-all-attachments>
              }
              @case ('offboarding') {
              <app-maintenance-offboarding [formGroup]="offboarding" [readonly]="isReadOnly"
                (save)="saveSection($event)" (preview)="previewAttachment($event)"></app-maintenance-offboarding>
              }
              }
            </div>
          </div>
        </div>
        }

      </div>
    </div>
    <!-- <br> -->
    @if (showPreview) {
    <div class="preview-backdrop" appDocumentPreviewOverlay (dismiss)="closePreview()">

      <div class="preview-panel" role="dialog" aria-modal="true" aria-label="Document preview">

        <!-- Header -->
        <div class="preview-header d-flex justify-content-between align-items-center p-3 border-bottom">
          <div class="d-flex flex-column">
            <span class="fw-bold fs-5">Document Preview</span>
            <small class="text-muted">{{ currentFileName }}</small>
          </div>
          <div class="preview-header-actions">
            @if (!isPdf) {
            <button mat-icon-button (click)="zoomOut()" [disabled]="zoomLevel <= MIN_ZOOM" title="Zoom out">
              <mat-icon>zoom_out</mat-icon>
            </button>
            <span class="zoom-level">{{ zoomLevel * 100 }}%</span>
            <button mat-icon-button (click)="zoomIn()" [disabled]="zoomLevel >= MAX_ZOOM" title="Zoom in">
              <mat-icon>zoom_in</mat-icon>
            </button>
            }
            <button mat-icon-button color="warn" (click)="closePreview()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <!-- Preview Content -->
        <div class="preview-content">
          @if (isPdf) {
          <!-- <embed [src]="selectedDocument"  width="100%" height="600px" /> -->
          <iframe [src]="selectedDocument" class="preview-pdf"></iframe>

          } @else {
          <img [src]="selectedDocument" alt="Document Preview" class="preview-image"
            [style.transform]="'scale(' + zoomLevel + ')'" />
          }
        </div>

      </div>
    </div>
    }
  </div>
</app-pias-layout>